import { useCallback, useEffect, useRef, useState } from "react";
import { getGhgReferences } from "./referencesApi";

export const REFERENCES_ERROR = "Couldn't load the references.";

/**
 * The reference library with usage counts. `reload` refreshes it in the
 * background after a change, keeping the list on screen; a response that
 * arrives after a newer request is dropped.
 *
 * @returns {{ references: import("./referencesApi").GhgReference[], loading: boolean, error: string, reload: () => Promise<void> }}
 */
export function useGhgReferences() {
  const [references, setReferences] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const fetchIdRef = useRef(0);

  const reload = useCallback(async () => {
    const fetchId = ++fetchIdRef.current;
    setError("");
    try {
      const res = await getGhgReferences();
      if (fetchId !== fetchIdRef.current) return;
      const rows = res?.isSuccess === false ? null : res?.data?.data;
      if (Array.isArray(rows)) setReferences(rows);
      else setError(res?.data?.error || REFERENCES_ERROR);
    } catch {
      if (fetchId === fetchIdRef.current) setError(REFERENCES_ERROR);
    } finally {
      if (fetchId === fetchIdRef.current) setLoading(false);
    }
  }, []);

  useEffect(() => {
    reload();
    return () => {
      fetchIdRef.current += 1;
    };
  }, [reload]);

  return { references, loading, error, reload };
}
